import React from "react";
import { Plus, Trash2 } from "lucide-react";
import Button from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/Dialog";

interface AssetVersion {
  id: string;
  ad_title?: string | null;
  assets_row_id?: string;
  status?: string;
  content_json?: Record<string, any> | null;
  objective_json?: Record<string, any> | null;
  media_json?: Record<string, any> | null;
}

interface AdBuilderVersionChipsProps {
  assetVersions: AssetVersion[];
  activeVersionId: string | null;
  versionToDelete: { id: string; title: string } | null;
  onSelect: (versionId: string) => void;
  onCreate: (assetsRowId: string) => void;
  onDeleteRequest: (version: { id: string; title: string }) => void;
  onDeleteConfirm: (versionId: string) => void;
  onDeleteCancel: () => void;
}

const AdBuilderVersionChips: React.FC<AdBuilderVersionChipsProps> = ({
  assetVersions,
  activeVersionId,
  versionToDelete,
  onSelect,
  onCreate,
  onDeleteRequest,
  onDeleteConfirm,
  onDeleteCancel,
}) => {
  if (assetVersions.length === 0) return null;

  const chipColor = "#F5A623";
  const assetsRowId = assetVersions[0]?.assets_row_id;

  return (
    <>
      <div className="flex items-center gap-3 px-1 pb-4 flex-wrap">
        {assetVersions.map((version, index) => {
          const isActive = activeVersionId
            ? activeVersionId === version.id
            : index === 0;
          return (
            <button
              key={version.id}
              onClick={() => onSelect(version.id)}
              style={{
                borderColor: isActive ? chipColor : `${chipColor}66`,
                backgroundColor: isActive ? `${chipColor}1A` : "white",
              }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border-2 transition-colors"
            >
              <span
                style={{
                  backgroundColor: isActive ? chipColor : `${chipColor}66`,
                }}
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
              >
                {index + 1}
              </span>
              <span className="text-sm font-medium text-gray-800">
                {version.ad_title ?? `Asset ${index + 1}`}
              </span>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteRequest({
                    id: version.id,
                    title: version.ad_title ?? `Asset ${index + 1}`,
                  });
                }}
                style={{
                  color: isActive ? chipColor : `${chipColor}99`,
                }}
                className="ml-1 hover:opacity-70 transition-opacity"
              >
                <Trash2 size={13} />
              </span>
            </button>
          );
        })}

        {/* Add version button */}
        {assetsRowId && (
          <button
            onClick={() => onCreate(assetsRowId)}
            style={{ borderColor: chipColor, color: chipColor }}
            className="w-8 h-8 rounded-full border-2 flex items-center justify-center hover:opacity-70 transition-opacity"
          >
            <Plus size={16} />
          </button>
        )}
      </div>

      {/* Delete dialog */}
      <Dialog open={!!versionToDelete} onOpenChange={onDeleteCancel}>
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <div className="font-semibold text-xl text-center">
              Delete Version
            </div>
            <DialogDescription className="mt-4 text-center text-gray-600">
              Are you sure you want to delete{" "}
              <span className="font-semibold">{versionToDelete?.title}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-4 mt-6">
            <Button
              type="secondary"
              onClick={async () => {
                if (versionToDelete) {
                  await onDeleteConfirm(versionToDelete.id);
                }
              }}
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdBuilderVersionChips;
